export function documentFormat(file) {
  return String(
    file.fileSuffix ||
      String(file.originalName || "")
        .split(".")
        .pop() ||
      ""
  )
    .replace(/^\./, "")
    .toLowerCase();
}

export function documentCoverKey(file, session = "") {
  let source = file.fileKey || file.id;
  if (!source) {
    try {
      const url = new URL(file.url || file.previewUrl);
      // 临时签名刷新不代表内容更新；保留其他查询参数（例如对象版本）。
      for (const key of [...url.searchParams.keys()]) {
        if (
          /^(ossaccesskeyid|signature|expires|security-token|x-oss-.*|x-amz-.*)$/i.test(
            key
          )
        )
          url.searchParams.delete(key);
      }
      url.hash = "";
      source = url.href;
    } catch (_) {
      source = file.url || file.previewUrl;
    }
  }
  if (!source) return "";
  return JSON.stringify([
    session,
    source,
    documentFormat(file),
    file.updateTime || file.updatedAt,
    file.etag || file.eTag,
    file.versionId || file.version,
    file.fileSize,
  ]);
}

export function createDocumentCoverCache({
  maxEntries = 60,
  maxBytes = 12 * 1024 * 1024,
  maxAge = 10 * 60 * 1000,
  now = Date.now,
} = {}) {
  const entries = new Map();
  let bytes = 0;
  const remove = (key) => {
    const entry = entries.get(key);
    if (entry) {
      bytes -= entry.bytes;
      entries.delete(key);
    }
  };
  return {
    get(key) {
      const entry = entries.get(key);
      if (!entry) return null;
      if (now() - entry.created >= maxAge) {
        remove(key);
        return null;
      }
      entries.delete(key);
      entries.set(key, entry);
      return entry.cover;
    },
    set(key, cover) {
      if (!key || (!cover.image && !cover.text)) return;
      const size =
        2 *
        ((cover.image || "").length + (cover.text || "").length + key.length);
      if (size > maxBytes) return;
      for (const [oldKey, entry] of entries)
        if (now() - entry.created >= maxAge) remove(oldKey);
      remove(key);
      entries.set(key, {
        cover: Object.freeze({ ...cover }),
        bytes: size,
        created: now(),
      });
      bytes += size;
      while (entries.size > maxEntries || bytes > maxBytes)
        remove(entries.keys().next().value);
    },
    clear() {
      entries.clear();
      bytes = 0;
    },
  };
}

// 仅驻留当前页面内存，不保存原始文档、签名地址或文档内容到浏览器持久存储。
export const documentCoverCache = createDocumentCoverCache();

// 限制首屏同时解析的文档数，翻页或关闭时跳过已取消的任务。
let active = 0;
const queue = [];
export function queueDocumentCover(task, signal) {
  return new Promise((resolve, reject) => {
    queue.push({ task, signal, resolve, reject });
    drain();
  });
}
function drain() {
  while (active < 2 && queue.length) {
    const job = queue.shift();
    if (job.signal.aborted) {
      job.reject(new Error("cancelled"));
      continue;
    }
    active++;
    Promise.resolve()
      .then(job.task)
      .then(job.resolve, job.reject)
      .finally(() => {
        active--;
        drain();
      });
  }
}

export async function readTextCover(url, signal) {
  const response = await fetch(url, { signal });
  if (!response.ok || !response.body) throw new Error("无法读取文本");
  const reader = response.body.getReader();
  const decoder = new TextDecoder("utf-8");
  let text = "";
  let bytes = 0;
  try {
    while (bytes < 65536 && text.length < 1200) {
      const { done, value } = await reader.read();
      if (done) break;
      bytes += value.byteLength;
      text += decoder.decode(
        value.subarray(0, Math.max(0, 65536 - (bytes - value.byteLength))),
        { stream: true }
      );
    }
    text += decoder.decode();
  } finally {
    await reader.cancel().catch(() => {});
  }
  return text.replace(/^\uFEFF/, "").slice(0, 1200);
}

export async function loadPdfLibrary() {
  const pdfjs = await import(
    /* webpackChunkName: "document-cover-pdf" */ "pdfjs-dist/legacy/build/pdf.mjs"
  );
  const base = `${process.env.BASE_URL || "/"}pdf-cover-assets/`;
  pdfjs.GlobalWorkerOptions.workerSrc = `${base}pdf.worker.${pdfjs.version}.min.js`;
  return pdfjs;
}

// PDF 的页面单位是 point，浏览器 100% 显示时按 96 CSS px / 72 point 换算。
export async function getPdfPageWidth(url, signal) {
  const pdfjs = await loadPdfLibrary();
  if (signal.aborted) throw new Error("cancelled");
  const task = pdfjs.getDocument({
    url,
    disableAutoFetch: true,
    disableStream: true,
    isEvalSupported: false,
  });
  const abort = () => { task.destroy().catch(() => {}); };
  signal.addEventListener("abort", abort, { once: true });
  try {
    const pdf = await task.promise;
    const page = await pdf.getPage(1);
    return page.getViewport({ scale: 96 / 72 }).width;
  } finally {
    signal.removeEventListener("abort", abort);
    await task.destroy();
  }
}

export async function renderPdfCover(url, canvas, signal) {
  const pdfjs = await loadPdfLibrary();
  if (signal.aborted) throw new Error("cancelled");
  const base = `${process.env.BASE_URL || "/"}pdf-cover-assets/`;
  const task = pdfjs.getDocument({
    url,
    cMapUrl: `${base}cmaps/`,
    cMapPacked: true,
    standardFontDataUrl: `${base}standard_fonts/`,
    wasmUrl: `${base}wasm/`,
    disableAutoFetch: true,
    disableStream: true,
    isEvalSupported: false,
  });
  const abort = () => {
    task.destroy().catch(() => {});
  };
  signal.addEventListener("abort", abort, { once: true });
  try {
    const pdf = await task.promise;
    const page = await pdf.getPage(1);
    if (signal.aborted) throw new Error("cancelled");
    const size = page.getViewport({ scale: 1 });
    const viewport = page.getViewport({
      scale: Math.min(360 / size.width, 480 / size.height),
    });
    canvas.width = Math.ceil(viewport.width);
    canvas.height = Math.ceil(viewport.height);
    await page.render({ canvasContext: canvas.getContext("2d"), viewport })
      .promise;
  } finally {
    signal.removeEventListener("abort", abort);
    await task.destroy();
  }
}
